import {
  Language, LocalizedRichDescription, LocalizedString, isNullOrUndefined,
  LocalizedPhotoReference, isNullOrUndefinedOrEmpty, GenericBaseClass,
  AbstractGenericBaseClass, getAnyFromLocalizationMap
} from 'in-time-core';
import { LanguageService } from '../../services';
import { inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HashMap, TranslocoService } from '@ngneat/transloco';
import { Observable, of, switchMap } from 'rxjs';

/**
 * Mixin for components that need to localize text.
 * Signal compatible version.
 *
 * @see LocalizationHelper for standalone implementation.
 *
 * @param BaseClass use EmptyClass if you don't have a base class
 * @returns an abstract class that you need to extend.
 */
export function LocalizationMixinSignals<TClass, TBase extends GenericBaseClass<TClass> | AbstractGenericBaseClass<TClass>>(BaseClass: TBase) {
  abstract class Result extends BaseClass {
    protected readonly languageService = inject(LanguageService);
    protected readonly translocoSerivce = inject(TranslocoService);

    protected readonly locale = toSignal(this.languageService.locale$, {
      initialValue: this.languageService.locale
    });

    protected readonly language = toSignal(this.languageService.language$, {
      initialValue: this.languageService.language
    });

    mustGetLocalizedText(text: LocalizedString): string {
      const language = this.locale().languageCode as Language;
      return text.getAnyOrEmpty(language);
    }

    getLocalization$(key: string, params?: HashMap): Observable<string> {
      return this.translocoSerivce.selectTranslate(key, params);
    }

    getLocalizationOrFallback$(key: string, fallbackKey: string, params?: HashMap): Observable<string> {
      return this.translocoSerivce
        .selectTranslate(key, params)
        .pipe(
          switchMap((value) => isNullOrUndefined(value) ?
            this.translocoSerivce.selectTranslate(fallbackKey, params)
            : of(value)
          ));
    }

    getLocalizedT<T>(map: Map<Language, T> | null | undefined): T | null {
      if(isNullOrUndefined(map)) {
        return null;
      }

      const language = this.locale().languageCode as Language;

      return getAnyFromLocalizationMap(map, language);
    }

    getLocalizedText(text: LocalizedString | null | undefined): string | null {
      if(isNullOrUndefined(text)) {
        return null;
      }

      const language = this.locale().languageCode as Language;
      return text.getAny(language);
    }

    getLocalizedTextOrdered(text: LocalizedString | null | undefined): string | null {
      if(isNullOrUndefined(text)) {
        return null;
      }

      const language = this.locale().languageCode as Language;
      let textOfLanguage = text.get(language);

      if(isNullOrUndefinedOrEmpty(textOfLanguage?.trim())) {
        textOfLanguage = text.get(Language.Hungarian);
      }

      if(isNullOrUndefined(textOfLanguage?.trim())) {
        return text.getAny();
      }

      return textOfLanguage;
    }

    getLocalizedRichDescriptionOrdered(description: LocalizedRichDescription | null): { text: string, plainText: string, length: number } | null {
      if(isNullOrUndefined(description)) {
        return null;
      }

      const language = this.locale().languageCode as Language;
      let descriptionOfLanguage = description.get(language);

      if(isNullOrUndefinedOrEmpty(descriptionOfLanguage?.text.trim())) {
        descriptionOfLanguage = description.get(Language.Hungarian);
      }

      if(isNullOrUndefinedOrEmpty(descriptionOfLanguage?.text.trim())) {
        return this.getLocalizedRichDescription(description);
      }

      const rawText = descriptionOfLanguage?.text;

      if(isNullOrUndefined(descriptionOfLanguage) || isNullOrUndefinedOrEmpty(rawText)) {
        return null;
      }

      if(rawText.trim().length === 0) {
        return null;
      }

      return {
        text: descriptionOfLanguage.richText,
        length: rawText.length,
        plainText: rawText,
      };
    }

    getLocalizedRichDescription(description: LocalizedRichDescription | null): { text: string, plainText: string, length: number } | null {
      if(isNullOrUndefined(description)) {
        return null;
      }

      const language = this.locale().languageCode as Language;
      const descriptionOfLanguage = description.getAny(language);
      const rawText = descriptionOfLanguage?.text;

      if(isNullOrUndefined(descriptionOfLanguage) || isNullOrUndefined(rawText)) {
        return null;
      }

      if(rawText.trim().length === 0) {
        return null;
      }

      return {
        text: descriptionOfLanguage.richText,
        length: rawText.length,
        plainText: rawText,
      };
    }

    getLocalizedPhoto(photo: LocalizedPhotoReference): string | null {
      const language = this.locale().languageCode as Language;
      const localizedPhotoUrl = photo.getAny(language);

      return localizedPhotoUrl?.downloadUrl ?? null;
    }


    getLocalizedPhotoOrdered(photo: LocalizedPhotoReference): string | null {
      const language = this.locale().languageCode as Language;
      let localizedPhotoUrl = photo.get(language);

      if(isNullOrUndefined(localizedPhotoUrl)) {
        localizedPhotoUrl = photo.get(Language.Hungarian);
      }

      if(isNullOrUndefined(localizedPhotoUrl)) {
        return photo.getAny(language)?.downloadUrl ?? null;
      }

      return localizedPhotoUrl?.downloadUrl ?? null;
    }
  }

  return Result;
}
